import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api/index";
import { useNotification } from "~hooks/useNotification";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";

export const useFetchTeamGroup = () => {
  const { fetchData } = useApi();
  const { organizationId, sport, clubId } = useParams();

  return useQuery({
    queryKey: ["teamGroups", organizationId, clubId],
    queryFn: async () => {
      const result = await fetchData({
        url: `${sport}/o/${organizationId}/entityGroups/${clubId}`,
      });
      return result?.data?.[0];
    },
    enabled: !!clubId,
  });
};

const defaultParams = { sortBy: "nameFullLocal" }

export const useFetchTeamGroupsPagination = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport } = useParams();
  let URL = `${sport}/o/${organizationId}/entityGroups`;
  const mergedParams = { ...defaultParams, ...params }
  if (!isEmpty(mergedParams)) {
    URL += `?${new URLSearchParams(mergedParams)?.toString()}`;
  }

  return useQuery({
    queryKey: ["teamGroups", organizationId, JSON.stringify(mergedParams)],
    queryFn: async () => {
      return await fetchData({
        url: URL,
      });
    },
  });
};

export const useFetchTeamGroups = (params = {}, enabled) => {
  const { fetchData } = useApi();
  const { organizationId, sport } = useParams();
  let URL = `${sport}/o/${organizationId}/entityGroups`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["teamGroups", organizationId, JSON.stringify(params)],
    queryFn: async () => {
      const result = await fetchData({
        url: URL,
      });
      return result?.data || [];
    },
    enabled: !!enabled,
  });
};

export const useDeleteTeamGroup = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  
  return useMutation({
    mutationFn: async (entityGroupId) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/entityGroups/${entityGroupId}`,
        method: "DELETE",
      });
    },
    onSuccess: async () => {
      notification.success({
        message: formatMessage({
          id: "club.delete.success",
          defaultMessage: "Club deleted successfully",
        }),
        placement: "bottomRight",
      });

      await Promise.all([
        invalidateCache(),
        queryClient.invalidateQueries({ queryKey: ["teamGroups"] }),
      ]);
    },
  });
};

export const useCreateTeamGroup = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();

  return useMutation({
    mutationFn: async (data) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/entityGroups`,
        method: "POST",
        data,
      });
    },
    onSuccess: async () => {
      notification.success({
        message: formatMessage({
          id: "club.create.success",
          defaultMessage: "Conferences created successfully",
        }),
        placement: "bottomRight",
      });
      
      await Promise.all([
        invalidateCache(),
        queryClient.invalidateQueries({ queryKey: ["teamGroups"] }),
      ]);
    },
  });
};

export const useUpdateTeamGroup = () => {
  const { sport, organizationId, clubId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  
  return useMutation({
    mutationFn: async (data) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/entityGroups/${clubId}`,
        method: "PUT",
        data,
      });
    },
    onSuccess: async () => {
      notification.success({
        message: formatMessage({
          id: "club.update.success",
          defaultMessage: "Club updated successfully",
        }),
        placement: "bottomRight",
      });
      
      await Promise.all([
        invalidateCache(),
        queryClient.invalidateQueries({ queryKey: ["teamGroups"] }),
      ]);
    },
  });
};
